import type { AsyncData, UseFetchOptions } from "#app";
import { routes } from "~/constants/routes";
import { accessTokenUrl } from "~/services/const";

let jwt: string | null = useAuthState.value.jwt;
let jwtPromise: Promise<string> | null = null;

export async function useSFetch<T, E = any>(
  url: string,
  options: UseFetchOptions<T> = {}
): Promise<AsyncData<T, E>> {
  return new Promise((resolve, reject) => {
    const account = useCookie("account").value;

    if (account) {
      fetchJWT(account)
        .then(async (jwt) => {
          const defaults: UseFetchOptions<T> = {
            ...options,
            headers: { Authorization: `JWT ${jwt}` },
          };
          const res = await useFetch(url, defaults);

          if (res.error.value) {
            // Token is expired. Fetch JWT from auth server.
            if (res.error.value.statusCode === 401) {
              fetchJWT(account, true)
                .then(async (j) => {
                  const fetch = await useFetch(url, {
                    ...options,
                    headers: { Authorization: `JWT ${j}` },
                  });
                  resolve(fetch as AsyncData<T, E>);
                })
                .catch(reject);
            } else {
              resolve(res as AsyncData<T, E>);
            }
          } else {
            resolve(res as AsyncData<T, E>);
          }
        })
        .catch(console.error);
    } else {
      redirectToLogin();
    }
  });
}

function fetchJWT(account: string, force = false): Promise<string> {
  if (jwt && !force) {
    return Promise.resolve(jwt);
  }
  if (jwtPromise && !force) {
    return jwtPromise;
  }
  jwtPromise = new Promise((resolve, reject) => {
    useFetch(accessTokenUrl, {
      credentials: "include",
      params: { account },
      onResponse: async ({ response }) => {
        if (response.status === 200) {
          jwt = response._data.token as string;

          useAuthState.value.jwt = jwt;
          resolve(jwt);
        }
      },
      onResponseError({ response }) {
        if ([403, 400, 404].includes(response.status)) {
          redirectToLogin();
        }
        jwtPromise = null;
        reject("error: fetch jwt rejected");
      },
    });
  });
  return jwtPromise;
}

function redirectToLogin() {
  const currentUrl = new URL(window.location.href);
  currentUrl.search = "";

  useCookie("account").value = null;
  const accountsLoginUrl = `${routes.auth.login.root}`;

  navigateTo(accountsLoginUrl, { external: true, replace: true });
}
